import { Component, inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../../services/authentication/auth.service';
import { CommonModule } from '@angular/common';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { SplitNamePipe } from '../../../shared/pipes/split-name/split-name.pipe';
import { RouterModule } from '@angular/router';
import { AccountInfoService } from '../../../services/bidder-community/account-info/account-info.service';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { IAccountInfoDTO } from '../../../models/IAccountInfoDTO';
import { ToastMessageService } from '../../../services/toast-message/toast-message.service';
import { BidderCommunityMenuService } from '../../../services/bidder-community/bidder-community-menu/bidder-community-menu.service';
import { environment } from '../../../../environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';


@Component({
  selector: 'app-account-info',
  imports: [BreadcrumbModule,
    ReactiveFormsModule,
    ButtonModule,
    CommonModule,
    RouterModule,
    ProgressSpinnerModule],
  providers: [SplitNamePipe],
  templateUrl: './account-info.component.html',
  styleUrl: './account-info.component.scss'
})
export class AccountInfoComponent implements OnInit {

  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly accountService = inject(AccountInfoService);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(ToastMessageService);
  private readonly platformId = inject(PLATFORM_ID);


  isAuthenticated = this.oidcSecurityService.authenticated;
  isLoading = this.accountService.isLoadingData;


  items: MenuItem[] | undefined;
  form: FormGroup;
  accountInfo: IAccountInfoDTO | null = null;

  authSubscription!: Subscription;

  constructor(private fb: FormBuilder, private splitNamePipe: SplitNamePipe, private bidderCommunityMenuService: BidderCommunityMenuService) {
    this.form = this.fb.group({
      firstName: ['', [CustomValidators.required('First Name')]],
      lastName: ['', [CustomValidators.required('Last Name')]],
      email: ['', [CustomValidators.required('Email'), CustomValidators.email()]],
      nickname: ['', [CustomValidators.lettersNumbersWithoutSpaces('Nick Name')]],
      mobilePhone: ['', [CustomValidators.numberValidator('Mobile Phone Number')]]
    });
  }

  @Input() id!: number;

  ngOnInit(): void {
    setTimeout(() => {
      this.bidderCommunityMenuService.setPageData(
        {
          id: this.id!,
          pageName: "Account Info",
          pageRoute: "account-info"
        }
      )

      if (typeof window !== 'undefined') {
        window.onpageshow = function (event) {
          if (event.persisted) {
            window.location.reload();
          }
        };
      }

      if (this.isAuthenticated().isAuthenticated) {
        this.getAccountInfo();
      } else {
        if (isPlatformBrowser(this.platformId)) {
          if (typeof window !== 'undefined') {
            window.open(environment.mediaUrl, '_self');
          }
          return;
        }
      }

      this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
        if (isAuth) {
          this.getAccountInfo();
        } else {
          if (isPlatformBrowser(this.platformId)) {
            if (typeof window !== 'undefined') {
              window.open(environment.mediaUrl, '_self');
            }
          }
        }
      });
    },100);
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
  }


  getFieldError(field: string): string | null {
    const control = this.form.get(field);
    if (control && control.errors && (control.dirty || control.touched)) {
      const firstErrorKey = Object.keys(control.errors)[0];
      return control.errors[firstErrorKey];
    }
    return null;
  }

  getAccountInfo() {
    this.accountService.getAccountInfo().subscribe(({
      next: () => {
        this.accountInfo = this.accountService.accountInfoSignal();
        // console.log(this.accountInfo);
        if (this.accountInfo) {
          this.form.patchValue({
            firstName: this.accountInfo.firstName,
            lastName: this.accountInfo.lastName,
            email: this.accountInfo.email,
            mobilePhone: this.accountInfo.mobilePhone,
            nickname: this.accountInfo.nickname
          });

          if (this.accountInfo.hasGuid) {
            this.form.get('firstName')?.disable();
            this.form.get('lastName')?.disable();
            this.form.get('email')?.disable();
          }
        }
      },
      error: (err) => {
        console.log('Error retreve data', err);
      }
    }))
  }

  onSubmit() {

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData: IAccountInfoDTO = {
      ...this.form.getRawValue(),
      hasGuid: this.accountInfo?.hasGuid
    }

    this.accountService.putAccountInfo(formData).subscribe({
      next: () => {
        this.messageService.success(`Your account information has been saved.`);
      },
      error: (err) => {
        let errorMsg = 'Unknown error';

        try {
          const parsed = typeof err.error === 'string' ? JSON.parse(err.error) : err.error;

          if (parsed?.errors) {
            Object.values(parsed.errors).forEach(msg => this.messageService.error(msg as string));
            return;
          }

          if (parsed?.message) {
            errorMsg = parsed.message;
          } else if (parsed?.errorMessage) {
            errorMsg = parsed.errorMessage;
          }

        } catch {
          errorMsg = typeof err.error === 'string' ? err.error : errorMsg;
        }

        this.messageService.error(errorMsg || 'Unknown error');
      }

    })

  }


}
